import { useEffect, useState } from "react";
import AccountsSkeleton from "components/common/loaders/AccountsSkeleton";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import Skeleton from "components/common/loaders/Skeleton";
import CheckoutExitPopup from "components/common/popups/CheckoutExitPopup";
import AppRouter from "components/layout/AppRouter";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLoader } from "lib/contexts/LoaderContext";
import { useUserContext } from "lib/contexts/UserProvider";
import { useCartItemVariants } from "lib/hooks/useCartItemVariants";
import { initialiseClarity } from "lib/third-party/clarity";
import { refreshExpiredIdToken } from "lib/utils/auth";
import { ChannelName, CheckoutMessageTypes, createBroadcastChannel } from "lib/utils/broadcastChannel";
import { createFloSessionCookie, exitCheckout, getOriginLocalStorage } from "lib/utils/helpers";
import { Toaster } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

function App() {
  const {
    state: { isAuthenticating },
    actions: { setLoggedIn, setIsAuthenticating },
  } = useAuthContext();
  const {
    state: { redirectUrl },
    actions: { setCheckoutId },
  } = useCheckoutContext();
  const {
    actions: { setUserData, deleteAddressCache, updateUser },
  } = useUserContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page")?.toLowerCase();

  const [openCheckoutExitPopup, setOpenCheckoutExitPopup] = useState<boolean>(false);

  const { isLoading } = useLoader();

  // Loads sibling variants for every cart product once, so item cards can render their picker.
  useCartItemVariants();

  useEffect(() => {
    initialiseClarity();
    revalidateRefreshToken();
    createFloSessionCookie();
    handleBroadcastChannel();
    (window as any).getOriginLocalStorage = getOriginLocalStorage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleBroadcastChannel = () => {
    const channel = createBroadcastChannel(ChannelName.SHOPFLO_CHANNEL);
    channel.onMessage((message) => {
      if (message.type === CheckoutMessageTypes.USER_LOGGED_OUT) {
        setCheckoutId("");
        setUserData({
          uid: "",
          email: "",
          phone: "",
          name: "",
          default_billing_address: undefined,
          default_shipping_address: undefined,
          addresses: undefined,
          hasHomeAddr: false,
          hasWorkAddr: false,
        });
        deleteAddressCache();
      }
    });
    return () => {
      channel.close();
    };
  };

  const revalidateRefreshToken = async () => {
    const presignedRefreshtToken = searchParams.get("auth");
    const forceRefresh = searchParams.get("forceRefresh");
    const { isLoggedIn, newTokensGenerated = false } = await refreshExpiredIdToken(
      presignedRefreshtToken ?? undefined,
      forceRefresh === "true" || Boolean(presignedRefreshtToken),
    );
    if (forceRefresh === "true" && isLoggedIn) {
      // remove forceRefresh and auth from url
      setSearchParams((prev) => {
        prev.delete("auth");
        prev.delete("forceRefresh");
        return prev;
      });
    }
    setLoggedIn(isLoggedIn, {
      loginType: isLoggedIn ? "COOKIE" : undefined,
    });
    setIsAuthenticating(false);
    if (newTokensGenerated) {
      updateUser();
    }
  };

  // const onBackButtonEvent = useCallback(
  //   (e: any) => {
  //     e.preventDefault();
  //     if (checkoutModal !== "NONE") return;
  //     if (checkoutView === "PAYMENTS") {
  //       setCheckoutViewCtx("ADDRESS_LIST");
  //       return;
  //     }
  //     if (checkoutView !== "ADDRESS_LIST") return;
  //     setOpenCheckoutExitPopup(true);
  //   },
  //   [checkoutView, checkoutModal],
  // );

  // useBackButton(onBackButtonEvent);

  const onCloseCheckoutExitPopup = () => {
    window.history.pushState(null, "", null);
    setOpenCheckoutExitPopup(false);
    setTimeout(() => {
      document?.documentElement?.style?.removeProperty("overflow");
    }, 500);
  };

  const handleBrowserBackEvent = async () => {
    setOpenCheckoutExitPopup(false);
    exitCheckout(redirectUrl);
  };

  /**
   * Not showing this skeleton loader for checkout page
   * as we handle it on component level
   * Also page remains undefined for checkout page
   */
  return isAuthenticating && page && page !== "marketing-popup" ? (
    page === "account" ? (
      <AccountsSkeleton />
    ) : (
      <Skeleton />
    )
  ) : (
    <>
      <AppRouter />

      <Toaster />

      {Boolean(isLoading) && <OverlaySpinner />}

      <CheckoutExitPopup
        closeCheckout={handleBrowserBackEvent}
        isOpen={openCheckoutExitPopup}
        closePopup={onCloseCheckoutExitPopup}
      />
    </>
  );
}

export default App;
